import dayjs from "dayjs";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from "react";

import { useUserRoleAndSubscription } from "../hooks/useUserRoleAndSubscription";
import {
  getDocumentUploadedCounts,
  getLoginSuccessCounts,
  MixpanelDocumentUploadedCountsQuery,
  MixpanelDocumentUploadedCountsResponse,
  MixpanelSeriesPoint
} from "../api/mixpanelAnalytics";

type ChartPoint = {
  date: string;
  count: number;
};

type ComparisonChartPoint = {
  day: string;
  "This week": number;
  "Last week": number;
};

type WeeklyComparisonKpi = {
  currentTotal: number | null;
  prevTotal: number | null;
  delta: number | null;
  percent: number | null;
  trend: "up" | "down" | "neutral";
};

type WeeklyComparisonState = {
  current: MixpanelDocumentUploadedCountsResponse | null;
  previous: MixpanelDocumentUploadedCountsResponse | null;
  isLoading: boolean;
  error: string | null;
  kpi: WeeklyComparisonKpi;
  comparisonChartData: ComparisonChartPoint[];
};

type DateRange = {
  fromDate: string;
  toDate: string;
  previousFromDate: string;
  previousToDate: string;
};

type MixpanelAnalyticsContextValue = {
  dateRange: DateRange;
  documentUploads: WeeklyComparisonState;
  loginSuccess: WeeklyComparisonState;
  ensureLoaded: () => void;
  refreshDocumentUploads: () => void;
  refreshLoginSuccess: () => void;
  getUpdatedAgoLabel: (queryTimestamp?: string | null) => string | null;
};

const MixpanelAnalyticsContext =
  createContext<MixpanelAnalyticsContextValue | null>(null);

function normalizeSeriesPoint(p: MixpanelSeriesPoint): ChartPoint {
  const maybeAny = p as unknown as Record<string, unknown>;
  const date =
    (typeof maybeAny.date === "string" && maybeAny.date) ||
    (typeof maybeAny.day === "string" && (maybeAny.day as string)) ||
    (typeof maybeAny.time === "string" && (maybeAny.time as string)) ||
    "";

  const countRaw = maybeAny.count ?? maybeAny.value ?? 0;
  const count = typeof countRaw === "number" ? countRaw : Number(countRaw) || 0;

  return { date, count };
}

function buildKpi(
  current: MixpanelDocumentUploadedCountsResponse | null,
  previous: MixpanelDocumentUploadedCountsResponse | null
): WeeklyComparisonKpi {
  const currentTotal = current?.total ?? null;
  const prevTotal = previous?.total ?? null;

  if (currentTotal == null || prevTotal == null) {
    return {
      currentTotal,
      prevTotal,
      delta: null,
      percent: null,
      trend: "neutral"
    };
  }

  const delta = currentTotal - prevTotal;
  const percent = prevTotal === 0 ? null : delta / prevTotal;
  const trend = delta > 0 ? "up" : delta < 0 ? "down" : "neutral";
  return { currentTotal, prevTotal, delta, percent, trend };
}

function buildComparisonChartData(args: {
  current: MixpanelDocumentUploadedCountsResponse | null;
  previous: MixpanelDocumentUploadedCountsResponse | null;
  fromDate: string;
}): ComparisonChartPoint[] {
  const { current, previous, fromDate } = args;

  const currentSeries = (current?.series ?? [])
    .map(normalizeSeriesPoint)
    .filter((p) => p.date)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));

  const previousSeries = (previous?.series ?? [])
    .map(normalizeSeriesPoint)
    .filter((p) => p.date)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));

  return Array.from({ length: 7 }, (_, i) => {
    const currentCount = currentSeries[i]?.count ?? 0;
    const previousCount = previousSeries[i]?.count ?? 0;
    const currentDate =
      currentSeries[i]?.date ??
      dayjs(fromDate).add(i, "day").format("YYYY-MM-DD");
    const day = dayjs(currentDate).format("ddd");

    return {
      day,
      "This week": currentCount,
      "Last week": previousCount
    };
  });
}

function emptyWeeklyState(fromDate: string): WeeklyComparisonState {
  return {
    current: null,
    previous: null,
    isLoading: false,
    error: null,
    kpi: {
      currentTotal: null,
      prevTotal: null,
      delta: null,
      percent: null,
      trend: "neutral"
    },
    comparisonChartData: buildComparisonChartData({
      current: null,
      previous: null,
      fromDate
    })
  };
}

export const MixpanelAnalyticsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { isSuperAdmin } = useUserRoleAndSubscription();

  const dateRange: DateRange = useMemo(() => {
    const toDateLocal = dayjs().format("YYYY-MM-DD");
    const fromDateLocal = dayjs().subtract(6, "day").format("YYYY-MM-DD");

    const previousToDateLocal = dayjs(fromDateLocal)
      .subtract(1, "day")
      .format("YYYY-MM-DD");
    const previousFromDateLocal = dayjs(previousToDateLocal)
      .subtract(6, "day")
      .format("YYYY-MM-DD");

    return {
      fromDate: fromDateLocal,
      toDate: toDateLocal,
      previousFromDate: previousFromDateLocal,
      previousToDate: previousToDateLocal
    };
  }, []);

  const [documentUploads, setDocumentUploads] = useState<WeeklyComparisonState>(
    () => emptyWeeklyState(dateRange.fromDate)
  );
  const [loginSuccess, setLoginSuccess] = useState<WeeklyComparisonState>(() =>
    emptyWeeklyState(dateRange.fromDate)
  );

  const inFlight = useRef<{ uploads: boolean; logins: boolean }>({
    uploads: false,
    logins: false
  });
  const hasLoaded = useRef<{ uploads: boolean; logins: boolean }>({
    uploads: false,
    logins: false
  });

  const fetchWeeklyComparison = useCallback(
    async (args: {
      fetcher: (
        query: MixpanelDocumentUploadedCountsQuery
      ) => Promise<MixpanelDocumentUploadedCountsResponse>;
      force: boolean;
      key: "uploads" | "logins";
    }) => {
      const { fetcher, force, key } = args;

      const setState = key === "uploads" ? setDocumentUploads : setLoginSuccess;

      // Safety: never hit superadmin-only endpoints for non-superadmins.
      if (!isSuperAdmin) return;

      if (inFlight.current[key]) return;
      if (!force && hasLoaded.current[key]) return;

      inFlight.current[key] = true;
      setState((s) => ({ ...s, isLoading: true, error: null }));

      try {
        const [current, previous] = await Promise.all([
          fetcher({
            from_date: dateRange.fromDate,
            to_date: dateRange.toDate,
            unit: "day"
          }),
          fetcher({
            from_date: dateRange.previousFromDate,
            to_date: dateRange.previousToDate,
            unit: "day"
          })
        ]);

        const kpi = buildKpi(current, previous);
        const comparisonChartData = buildComparisonChartData({
          current,
          previous,
          fromDate: dateRange.fromDate
        });

        setState({
          current,
          previous,
          isLoading: false,
          error: null,
          kpi,
          comparisonChartData
        });
        hasLoaded.current[key] = true;
      } catch (e) {
        const message =
          e instanceof Error ? e.message : "Failed to load analytics";
        setState((s) => ({
          ...s,
          isLoading: false,
          error: message
        }));
      } finally {
        inFlight.current[key] = false;
      }
    },
    [dateRange, isSuperAdmin]
  );

  const ensureLoaded = useCallback(() => {
    if (!isSuperAdmin) return;
    fetchWeeklyComparison({
      fetcher: getDocumentUploadedCounts,
      force: false,
      key: "uploads"
    }).catch(() => {});
    fetchWeeklyComparison({
      fetcher: getLoginSuccessCounts,
      force: false,
      key: "logins"
    }).catch(() => {});
  }, [fetchWeeklyComparison, isSuperAdmin]);

  const refreshDocumentUploads = useCallback(() => {
    if (!isSuperAdmin) return;
    fetchWeeklyComparison({
      fetcher: getDocumentUploadedCounts,
      force: true,
      key: "uploads"
    }).catch(() => {});
  }, [fetchWeeklyComparison, isSuperAdmin]);

  const refreshLoginSuccess = useCallback(() => {
    if (!isSuperAdmin) return;
    fetchWeeklyComparison({
      fetcher: getLoginSuccessCounts,
      force: true,
      key: "logins"
    }).catch(() => {});
  }, [fetchWeeklyComparison, isSuperAdmin]);

  const getUpdatedAgoLabel = useCallback((queryTimestamp?: string | null) => {
    if (!queryTimestamp) return null;
    const ts = dayjs(queryTimestamp);
    if (!ts.isValid()) return null;
    const minutes = dayjs().diff(ts, "minute");
    if (minutes < 1) return "Updated just now";
    if (minutes < 60)
      return `Updated ${minutes} min${minutes === 1 ? "" : "s"} ago`;
    const hours = dayjs().diff(ts, "hour");
    if (hours < 24) return `Updated ${hours} hr${hours === 1 ? "" : "s"} ago`;
    const days = dayjs().diff(ts, "day");
    return `Updated ${days} day${days === 1 ? "" : "s"} ago`;
  }, []);

  const value: MixpanelAnalyticsContextValue = useMemo(
    () => ({
      dateRange,
      documentUploads,
      loginSuccess,
      ensureLoaded,
      refreshDocumentUploads,
      refreshLoginSuccess,
      getUpdatedAgoLabel
    }),
    [
      dateRange,
      documentUploads,
      loginSuccess,
      ensureLoaded,
      refreshDocumentUploads,
      refreshLoginSuccess,
      getUpdatedAgoLabel
    ]
  );

  return (
    <MixpanelAnalyticsContext.Provider value={value}>
      {children}
    </MixpanelAnalyticsContext.Provider>
  );
};

export function useMixpanelAnalytics() {
  const ctx = useContext(MixpanelAnalyticsContext);
  if (!ctx) {
    throw new Error(
      "useMixpanelAnalytics must be used within MixpanelAnalyticsProvider"
    );
  }
  return ctx;
}
